<template>
    <VariantsHeader @close="emit('close')" @save="saveVariantsSelection" />

    <hox-modal v-if="!hasPermissions">
        <hox-alert margin-top="base" type="danger" @close="emit('close')">
            <template #content>
                <p>It looks like you do not have the right permissions to see the library for this campaign.</p>
                <p>Ask your admin to grant you access and try again.</p>
            </template>
            <template #actionItems>
                <Button type="primary" @click="emit('close')">Got it</Button>
            </template>
        </hox-alert>
    </hox-modal>

    <template v-else>
        <hox-loading-layer v-if="isLoadingCampaignData" is-opaque :left-offset-pixels="60" size="large" />

        <div v-if="campaignLoadingError">
            <hox-alert margin-bottom="large" type="danger">
                <template #title>Uh oh!</template>
                <template #content>
                    <p>
                        There was an unexpected issue while loading library data. Hopefully it was a temporary issue and
                        should be fixed if you try again in a few moments.
                    </p>
                </template>
                <template #actionItems>
                    <Button type="primary" @click="fetchCampaignData">Retry</Button>
                </template>
            </hox-alert>
        </div>

        <template v-else>
            <div class="variations-wrap">
                <VariantsSidebar />

                <div v-if="!hasFilterOptionsForEachType">
                    <hox-empty-message v-if="userCanAccessEditor">
                        <template #title>This campaign doesn't have any ads</template>
                        <template #content>
                            <p>Head over to the editor to get started</p>
                        </template>
                    </hox-empty-message>
                    <hox-empty-message v-else>
                        <template #title>There are no ads in the library</template>
                    </hox-empty-message>
                </div>

                <div v-else class="variations-list">
                    <LoadingOverlay v-if="isLoadingDeliverables" />

                    <campaign-locked-notice />

                    <div v-if="!allFiltersAreActive" class="notification-container">
                        <hox-empty-message>
                            <template #title>No deliverables to show</template>
                            <template #content>
                                <p>
                                    You need to select at least one
                                    <em>language, size</em>
                                    and
                                    <em>group value for every group filter</em>
                                    in the sidebar on the left.
                                </p>
                            </template>
                        </hox-empty-message>
                    </div>

                    <div v-else-if="!deliverables.length && !isLoadingDeliverables" class="notification-container">
                        <EmptyMessage />
                    </div>

                    <hox-alert
                        v-else-if="deliverablesLoadingError"
                        margin-bottom="large"
                        type="danger"
                        class="notification-container"
                    >
                        <template #title>Uh oh!</template>
                        <template #content>
                            <p>
                                There was an unexpected issue while getting the deliverables. Hopefully it was a
                                temporary issue and should be fixed if you try again in a few moments.
                            </p>
                        </template>
                        <template #actionItems>
                            <Button type="primary" @click="fetchDeliverables">Retry getting deliverables</Button>
                        </template>
                    </hox-alert>

                    <div v-else ref="body" class="variation-previews-container">
                        <div
                            v-for="size in Object.keys(deliverableSizeGroups)"
                            :key="size"
                            class="variation-preview-group"
                        >
                            <VariantsPreview
                                v-for="(deliverable, index) in deliverableSizeGroups[size]"
                                :key="deliverable.idHash + index"
                                class="variation-preview"
                                :deliverable="deliverable"
                                :max-height="bodyObserver.height.value"
                                :max-width="bodyObserver.width.value"
                                :scaling-factor="scalingFactor"
                                :playback-namespace="VariantsPlaybackNamespace"
                            />
                        </div>
                    </div>

                    <PreviewGroupControls
                        v-if="deliverables.length"
                        :scaling="{ scalingFactor }"
                        :scrubbar="{
                            currentTime: playbackController.currentTime.value,
                            duration: playbackController.duration.value
                        }"
                        replay-button
                        :pagination="{
                            currentPage: selectedPage,
                            pageSize,
                            total: filtersStore.selectedVariations
                        }"
                        @replay="playbackController.replay"
                        @setScalingFactor="scalingFactor = $event"
                        @setBannerPlaybackTime="playbackController.seek"
                        @setPage="selectedPage = $event"
                    />
                </div>
            </div>
        </template>
    </template>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import { USER_ACTIONS } from "hoxton-shared/enums/user";

import type { ConstructedDeliverable } from "@/types/types";

import { apolloClient } from "@/apollo";
import PaginatedDeliverablesQuery from "@/apollo/queries/v2/PaginatedDeliverables.gql";
import type { GQLPaginatedDeliverable, GQLPagination } from "@/apollo/types";

import CampaignLockedNotice from "@/views/Campaign/CampaignLockedNotice.vue";
import EmptyMessage from "@/components/common/EmptyMessage.vue";
import PreviewGroupControls from "@/components/PlaybackControls/PreviewGroupControls.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";

import VariantsPreview from "@/Connect/components/VariantsSelect/VariantsSelectPreview.vue";
import VariantsHeader from "@/Connect/components/VariantsSelect//VariantsSelectHeader.vue";
import VariantsSidebar from "@/Connect/components/VariantsSelect//VariantsSelectSidebar.vue";

import { hasScope, userCan } from "@/services/Auth";
import { constructLibraryFiltersQueryVariables, formatTemplateSizeLabel } from "@/utils";
import { constructDeliverable } from "@/services/deliverables/getPaginatedDeliverables";
import { CampaignGetters } from "@/store/modules/campaign";

import { useConnectFiltersStore } from "@/Connect/store/filters";
import { useConnectStore } from "@/Connect/store";

import { BannerNamespace, usePlaybackController } from "@/components/PlaybackControls/usePlaybackController";

import { useBodyDimensions } from "@/hooks/useBodyDimensions";

/**
 * SETUP
 */
const VariantsPlaybackNamespace = BannerNamespace.Variants;

const props = defineProps<{
    platformAccountName: string;
    ruleName: string;
    isLoadingCampaignData: boolean;
    campaignLoadingError: boolean;
}>();

const emit = defineEmits<(e: "close" | "fetchCampaignData") => void>();

const route = useRoute();
const clientId = route.params.clientId as string;
const campaignId = route.params.campaignId as string;

const containerElementRef = useTemplateRef("body");
const bodyObserver = useBodyDimensions();

watch(containerElementRef, newElementRef => {
    if (!newElementRef) {
        return;
    }

    bodyObserver.observe(newElementRef);
});

const userCanAccessEditor = computed(() => userCan(USER_ACTIONS.canManageOverwrites, { clientId, campaignId }));
const hasPermissions = computed(() => {
    return hasScope({ clientId, campaignId }) && userCan(USER_ACTIONS.canPreviewDeliverable, { clientId, campaignId });
});

/**
 * GLOBAL STATE
 */
const store = useStore();
const connectStore = useConnectStore();
const filtersStore = useConnectFiltersStore();

const playbackController = usePlaybackController(VariantsPlaybackNamespace);

/**
 * LOCAL STATE
 */
const isLoadingDeliverables = ref(false);
const deliverablesLoadingError = ref(false);

const selectedPage = ref(1);
const pageSize = ref(10);

const scalingFactor = ref(0); // auto scaling

const deliverables = ref<ConstructedDeliverable[]>([]);
const deliverableSizeGroups = ref<Record<string, ConstructedDeliverable[]>>({});

/**
 * COMPUTED
 */
const hasFilterOptionsForEachType = computed(() => connectStore.hasFilterOptionsForEachType);
const allFiltersAreActive = computed(() => filtersStore.allFiltersAreActive);
const selectedFiltersForLibraryQuery = computed(() => filtersStore.selectedFiltersForLibraryQuery);

/**
 * METHODS
 */
function saveVariantsSelection() {
    const selectedFiltersForVariants = {
        languages: filtersStore.selectedLanguages,
        masterTemplateIds: filtersStore.selectedSizes,
        editableGroupValueIds: filtersStore.allSelectedGroupIds
    };

    connectStore.setVariantFilters(props.platformAccountName, props.ruleName, selectedFiltersForVariants);

    /** we need reset the filter store since we want it "instanced" for each platformAccount and rule */
    filtersStore.$reset();

    emit("close");
}

function fetchCampaignData() {
    emit("fetchCampaignData");
}

function resetDeliverables() {
    deliverables.value = [];
    deliverableSizeGroups.value = {};
}

/**
 * DATA FETCHING
 */

async function fetchDeliverables() {
    isLoadingDeliverables.value = true;
    deliverablesLoadingError.value = false;

    if (!allFiltersAreActive.value) {
        console.error("Can't fetch, not all filter options are selected");
        resetDeliverables();

        return;
    }

    if (!selectedFiltersForLibraryQuery.value) {
        console.error("Can't fetch, no filters selected");
        resetDeliverables();

        return;
    }

    try {
        const libraryFilters = constructLibraryFiltersQueryVariables({
            selectedLanguages: filtersStore.selectedLanguages,
            selectedSizes: filtersStore.selectedSizes,
            selectedEditableIdsByGroup: filtersStore.selectedEditableIdsByGroup
        });

        const {
            data: { computeDeliverables: data }
        } = await apolloClient.query<{
            computeDeliverables: { deliverables: GQLPaginatedDeliverable[]; pagination: GQLPagination };
        }>({
            fetchPolicy: "no-cache",
            query: PaginatedDeliverablesQuery,
            variables: {
                campaignId,
                libraryFilters,
                page: selectedPage.value,
                perPage: pageSize.value
            }
        });

        const hasCreativeXEnabled = store.state.ui.currentClient?.creativeXPlatformSettings?.enabled ?? false;
        const hasSataliaEnabled = store.state.ui.currentClient?.sataliaPlatformSettings?.enabled ?? false;

        const constructedDeliverables = data.deliverables.map(deliverable =>
            constructDeliverable(
                deliverable,
                store.state.campaign.normalized.editables,
                store.getters[CampaignGetters.groupByGroupValueId],
                store.state.campaign.normalized.masterTemplates,
                hasCreativeXEnabled,
                hasSataliaEnabled
            )
        );

        deliverables.value = constructedDeliverables;

        deliverableSizeGroups.value = constructedDeliverables.reduce(
            (acc, deliverable) => {
                const size = formatTemplateSizeLabel(deliverable.masterTemplate);
                acc[size] = (acc[size] || []).concat(deliverable);

                return acc;
            },
            {} as Record<string, ConstructedDeliverable[]>
        );

        filtersStore.selectedVariations = data.pagination.totalCount;

        isLoadingDeliverables.value = false;
    } catch (error) {
        console.error(error);

        resetDeliverables();

        deliverablesLoadingError.value = true;
        isLoadingDeliverables.value = false;
    }
}

/**
 * SETUP LOGIC
 */
onMounted(() => {
    filtersStore.initGroupFiltersFromGroupOptions();

    const rule = connectStore.getRuleByName(props.platformAccountName, props.ruleName);

    if (rule?.variantsFilters) {
        filtersStore.setFiltersFromRule(rule.variantsFilters);
    }
});

onBeforeUnmount(() => {
    filtersStore.$reset();
});

watch(scalingFactor, newValue => {
    selectedPage.value = 1;

    switch (newValue) {
        case 1:
            pageSize.value = 10;
            break;
        case 0.75:
            pageSize.value = 20;
            break;
        case 0.5:
            pageSize.value = 30;
            break;
        case 0.25:
            pageSize.value = 40;
            break;
        default:
            pageSize.value = 10;
    }
});

watch([selectedPage, selectedFiltersForLibraryQuery, pageSize, allFiltersAreActive], () => {
    if (allFiltersAreActive.value) {
        fetchDeliverables();
    }
});
</script>

<style lang="css" scoped>
.variations-wrap {
    display: flex;

    height: calc(100vh - 60px - 52px);
}

.variations-list {
    position: relative;

    flex: 1;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    width: calc(100vw - 300px);
}

.notification-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    width: 100%;
    height: 100%;
}

.variation-previews-container {
    box-sizing: border-box;
    overflow-y: auto;

    width: 100%;
    height: 100%;

    padding: 20px 40px;

    display: flex;
    flex-direction: column;
    gap: 20px;
}

.variation-preview-group {
    width: 100%;

    display: flex;
    flex-wrap: wrap;
    gap: 15px;
}

.variation-preview {
    vertical-align: top;
    flex-grow: 0;
    flex-shrink: 0;
}
</style>
